import type { RecommendedSurfaceKey } from '../attentionRouting';
import {
  type DriveLaneViewModel,
  typeStateTotal,
} from './driveLaneAggregation';

export type DriveExitTarget = 'exopanel' | 'guidance';

export interface DriveStallExit {
  target: DriveExitTarget;
  reason: string;
}

/** Stall-point exit CTA when flow drop-off or structural gap (Drive design spec). */
export function resolveDriveStallExit(
  lane: DriveLaneViewModel,
  recommendedSurface?: RecommendedSurfaceKey
): DriveStallExit | null {
  const totals = lane.types.map(typeStateTotal);
  const laneTotal = totals.reduce((sum, n) => sum + n, 0);
  const first = totals[0] ?? 0;
  const last = totals.length > 0 ? totals[totals.length - 1]! : 0;

  if (lane.flowHint?.includes('drop-off') || (lane.hasFlow && first > 0 && last < first * 0.5)) {
    const target: DriveExitTarget =
      recommendedSurface === 'guidance' && laneTotal === 0 ? 'guidance' : 'exopanel';
    return {
      target,
      reason:
        target === 'exopanel'
          ? 'Items stalling in flow — advance from ExoPanel'
          : 'Structural gap in flow — plan in Guidance',
    };
  }

  if (laneTotal === 0 && lane.entropy !== 'active') {
    return {
      target: 'guidance',
      reason: 'Lane dormant or undefined — address planning in Guidance',
    };
  }

  if (lane.types.length >= 2 && first > 0 && last === 0) {
    return {
      target: recommendedSurface === 'guidance' ? 'guidance' : 'exopanel',
      reason: 'Acquisition flow stalls after first stage',
    };
  }

  return null;
}
